import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Headphones,
  Plus,
  MessageSquare,
  Send,
  ArrowLeft,
} from 'lucide-react';
import { SearchSelect } from '../../components/shared';
import {
  getVendorSupportTickets,
  getVendorSupportTicket,
  createVendorSupportTicket,
  replyToVendorSupportTicket,
} from '../../services/supportTicketService';
import { formatDate, formatTime } from '../../utils/dateUtils';
import { useToastContext } from '../../contexts/ToastContext';

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];

const CATEGORY_OPTIONS = [
  { value: 'general', label: 'General' },
  { value: 'technical', label: 'Technical' },
  { value: 'billing', label: 'Billing' },
  { value: 'feature_request', label: 'Feature Request' },
  { value: 'bug_report', label: 'Bug Report' },
];

const getStatusColor = (status) => {
  const config = {
    open: 'bg-blue-100 text-blue-800',
    in_progress: 'bg-yellow-100 text-yellow-800',
    resolved: 'bg-green-100 text-green-800',
    closed: 'bg-gray-100 text-gray-800',
  };
  return config[status] || 'bg-gray-100 text-gray-800';
};

const getPriorityColor = (priority) => {
  const config = {
    low: 'bg-green-100 text-green-800',
    medium: 'bg-yellow-100 text-yellow-800',
    high: 'bg-orange-100 text-orange-800',
    urgent: 'bg-red-100 text-red-800',
  };
  return config[priority] || 'bg-gray-100 text-gray-800';
};

const VendorPrioritySupport = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { success, error: showError } = useToastContext();
  const [view, setView] = useState('list'); // 'list' | 'create' | 'detail'
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [createForm, setCreateForm] = useState({
    subject: '',
    description: '',
    priority: 'medium',
    category: 'general',
  });
  const [replyText, setReplyText] = useState('');

  const { data: ticketsResponse, isLoading } = useQuery({
    queryKey: ['vendorSupportTickets'],
    queryFn: () => getVendorSupportTickets({ per_page: 50 }),
  });

  const { data: ticketResponse, isLoading: ticketLoading } = useQuery({
    queryKey: ['vendorSupportTicket', selectedTicketId],
    queryFn: () => getVendorSupportTicket(selectedTicketId),
    enabled: !!selectedTicketId && view === 'detail',
  });

  const createMutation = useMutation({
    mutationFn: createVendorSupportTicket,
    onSuccess: (data) => {
      queryClient.invalidateQueries(['vendorSupportTickets']);
      setView('detail');
      setSelectedTicketId(data?.data?.id);
      setCreateForm({ subject: '', description: '', priority: 'medium', category: 'general' });
      success('Support ticket created successfully');
    },
    onError: (err) => showError(err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Failed to create ticket'),
  });

  const replyMutation = useMutation({
    mutationFn: ({ id, data }) => replyToVendorSupportTicket(id, data),
    onSuccess: () => {
      setReplyText('');
      queryClient.invalidateQueries(['vendorSupportTicket', selectedTicketId]);
      queryClient.invalidateQueries(['vendorSupportTickets']);
      success('Reply sent successfully');
    },
    onError: (err) => showError(err?.response?.data?.message || 'Failed to send reply'),
  });

  const tickets = ticketsResponse?.data?.data || ticketsResponse?.data || [];
  const ticketsList = Array.isArray(tickets) ? tickets : tickets?.data || [];
  const ticket = ticketResponse?.data;

  const handleCreateTicket = (e) => {
    e.preventDefault();
    if (!createForm.subject.trim() || !createForm.description.trim()) return;
    createMutation.mutate(createForm);
  };

  const handleReply = (e) => {
    e.preventDefault();
    if (!replyText.trim()) return;
    replyMutation.mutate({ id: selectedTicketId, data: { response: replyText.trim() } });
  };

  const openDetail = (t) => {
    setSelectedTicketId(t.id);
    setView('detail');
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
      <div className="">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Headphones className="w-7 h-7" />
            Priority Support
          </h1>
          <p className="text-gray-600 mt-1">
            Create and manage support tickets. Our team typically responds within 24 hours.
          </p>
        </div>

        {view === 'list' && (
          <>
            <div className="flex justify-end mb-4">
              <button
                onClick={() => setView('create')}
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
              >
                <Plus className="w-5 h-5" />
                New Ticket
              </button>
            </div>

            <div className="bg-white rounded-lg border overflow-hidden">
              {isLoading ? (
                <div className="p-12 text-center">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600 mx-auto" />
                </div>
              ) : ticketsList.length === 0 ? (
                <div className="p-12 text-center">
                  <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-600 mb-4">No support tickets yet</p>
                  <button
                    onClick={() => setView('create')}
                    className="text-primary-600 hover:text-primary-700 font-medium"
                  >
                    Create your first ticket →
                  </button>
                </div>
              ) : (
                <div className="divide-y">
                  {ticketsList.map((t) => (
                    <div
                      key={t.id}
                      onClick={() => openDetail(t)}
                      className="p-4 hover:bg-gray-50 cursor-pointer flex items-center justify-between"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-gray-900 truncate">{t.subject}</p>
                        <p className="text-sm text-gray-500 truncate mt-0.5">{t.description}</p>
                        <div className="flex gap-2 mt-2">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(t.status)}`}>
                            {t.status?.replace('_', ' ')}
                          </span>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${getPriorityColor(t.priority)}`}>
                            {t.priority}
                          </span>
                        </div>
                      </div>
                      <div className="ml-4 text-right flex-shrink-0">
                        <p className="text-sm text-gray-500">{formatDate(t.created_at, 'medium')}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {view === 'create' && (
          <div className="bg-white rounded-lg border p-6">
            <button
              onClick={() => setView('list')}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
            >
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Create Support Ticket</h2>
            <form onSubmit={handleCreateTicket} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subject *</label>
                <input
                  type="text"
                  value={createForm.subject}
                  onChange={(e) => setCreateForm({ ...createForm, subject: e.target.value })}
                  placeholder="Brief description of your issue"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  required
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <SearchSelect
                    value={createForm.category}
                    onChange={(v) => setCreateForm({ ...createForm, category: v })}
                    options={CATEGORY_OPTIONS}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                  <SearchSelect
                    value={createForm.priority}
                    onChange={(v) => setCreateForm({ ...createForm, priority: v })}
                    options={PRIORITY_OPTIONS}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                <textarea
                  value={createForm.description}
                  onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
                  placeholder="Provide as much detail as possible..."
                  rows={5}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  required
                />
              </div>
              <div className="flex gap-3">
                <button
                  type="submit"
                  disabled={createMutation.isPending}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                >
                  {createMutation.isPending ? 'Creating...' : 'Create Ticket'}
                </button>
                <button
                  type="button"
                  onClick={() => setView('list')}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {view === 'detail' && ticket && (
          <div className="space-y-4">
            <button
              onClick={() => { setView('list'); setSelectedTicketId(null); }}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="w-4 h-4" /> Back to tickets
            </button>

            <div className="bg-white rounded-lg border p-6">
              {ticketLoading ? (
                <div className="p-8 text-center">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600 mx-auto" />
                </div>
              ) : (
                <>
                  <div className="flex flex-wrap gap-2 mb-4">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(ticket.status)}`}>
                      {ticket.status?.replace('_', ' ')}
                    </span>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getPriorityColor(ticket.priority)}`}>
                      {ticket.priority}
                    </span>
                    <span className="text-sm text-gray-500 capitalize">{ticket.category}</span>
                  </div>
                  <h2 className="text-lg font-semibold text-gray-900 mb-2">{ticket.subject}</h2>
                  <p className="text-gray-700 whitespace-pre-wrap mb-6">{ticket.description}</p>

                  <div className="border-t pt-6">
                    <h3 className="font-medium text-gray-900 mb-4">Conversation</h3>
                    <div className="space-y-4 mb-6">
                      {(ticket.ticket_replies || ticket.ticketReplies || []).map((reply) => (
                        <div
                          key={reply.id}
                          className={`p-4 rounded-lg ${
                            reply.response_type === 'admin'
                              ? 'bg-blue-50 border-l-4 border-blue-500'
                              : 'bg-gray-50 border-l-4 border-gray-300'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium text-gray-900">
                              {reply.response_type === 'admin' ? 'Support Team' : 'You'}
                            </span>
                            <span className="text-xs text-gray-500">
                              {formatDate(reply.created_at, 'medium')} {formatTime(reply.created_at)}
                            </span>
                          </div>
                          <p className="text-gray-700 whitespace-pre-wrap">
                            {reply.response_text || reply.response}
                          </p>
                        </div>
                      ))}
                    </div>

                    {!['resolved', 'closed'].includes(ticket.status) && (
                      <form onSubmit={handleReply} className="space-y-3">
                        <textarea
                          value={replyText}
                          onChange={(e) => setReplyText(e.target.value)}
                          placeholder="Type your reply..."
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                          disabled={replyMutation.isPending}
                        />
                        <button
                          type="submit"
                          disabled={!replyText.trim() || replyMutation.isPending}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                        >
                          <Send className="w-4 h-4" />
                          {replyMutation.isPending ? 'Sending...' : 'Send Reply'}
                        </button>
                      </form>
                    )}
                    {['resolved', 'closed'].includes(ticket.status) && (
                      <p className="text-sm text-gray-500 italic">This ticket is closed. Create a new ticket if you need further assistance.</p>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VendorPrioritySupport;
